<template>
  <!-- Полноэкранный блок при блокировке -->
  <div v-if="showBlockedScreen" class="blocked-fullscreen">
    <div class="blocked-scroll-inner">
      <div class="blocked-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" stroke-width="1.5"/>
          <path d="M7 11V7C7 4.24 9.24 2 12 2C14.76 2 17 4.24 17 7V11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </div>
      <h2 class="blocked-title">{{ $t('blocked.title') }}</h2>
      <div class="blocked-body">
        <p>{{ $t('blocked.greeting') }}</p>
        <p v-html="$t('blocked.reason')"></p>
        <p>{{ $t('blocked.action') }}</p>
        <p>{{ $t('blocked.contact') }}</p>
        <p class="blocked-footer">{{ $t('blocked.thanks') }}</p>
      </div>
      <button class="blocked-support-btn" @click="openSupport">
        HyperLiquid Support
      </button>
    </div>
  </div>

  <div v-else class="splash-container">
    <div class="splash-content">
      <!-- Логотип или анимация -->
      <div v-if="!showPinInput" class="splash-logo" :class="{ 'fade-out': shouldFadeOut }">
        <img
          :src="blobImageSrc"
          alt="Loading"
          class="blob-image"
        />
      </div>
      
      <!-- Индикатор загрузки -->
      <div v-if="!showPinInput && isLoading" class="loader-container">
        <div class="loader-dots">
          <div class="dot"></div>
          <div class="dot"></div>
          <div class="dot"></div>
        </div>
      </div>

      <!-- Форма ввода PIN-кода -->
      <div v-if="showPinInput" class="pin-section">
        <h2 class="pin-title">Enter your PIN</h2>
        <p class="pin-description">Please enter your PIN code to continue</p>
        <PinInput
          v-model="pin"
          :length="4"
          @complete="handlePinComplete"
        />
        <div v-if="pinError" class="error-message">
          {{ pinError }}
        </div>
        <button class="forgot-pin-button" @click="handleForgotPin">
          Forgot your PIN?
        </button>
      </div>

      <!-- Сообщение об ошибке -->
      <div v-if="errorMessage" class="error-banner">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="10" cy="10" r="9" stroke="currentColor" stroke-width="2"/>
          <path d="M10 6V10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          <path d="M10 14H10.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
        <span>{{ errorMessage }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useTelegramData } from '../composables/useTelegram.js'
import { apiService } from '../services/api.js'
import PinInput from '../components/ui/PinInput.vue'
import blobGreenGif from '../assets/blob_green.gif'
import { preloadComponents } from '../router/index.js'

export default {
  name: 'SplashScreen',
  components: {
    PinInput
  },
  setup() {
    const router = useRouter()
    const { userData, isLoading } = useTelegramData()
    const errorMessage = ref('')
    const isChecking = ref(false)
    const showPinInput = ref(false)
    const showBlockedScreen = ref(false)
    const pin = ref('')
    const pinError = ref('')
    const shouldFadeOut = ref(false)

    const checkUser = async () => {
      try {
        errorMessage.value = ''
        
        // Получаем user_id из Telegram
        const userId = userData.value.id
        
        if (!userId) {
          errorMessage.value = 'Unable to get user information'
          return
        }

        // Шаг 1: Проверяем существование пользователя
        const userDataResponse = await apiService.getUserData(userId)

        // Обработка ответа от /userdata
        if (userDataResponse.status === 200 || userDataResponse.status === 304) {
          // Проверяем блокировку доступа
          if (userDataResponse.data?.user?.access_blocked === true) {
            shouldFadeOut.value = true
            setTimeout(() => {
              showBlockedScreen.value = true
            }, 500)
            return
          }
          // Пользователь найден - проверяем наличие PIN
          await checkPin(userId)
        } else if (userDataResponse.status === 400) {
          // Ошибка запроса
          errorMessage.value = 'Internal server error'
        } else if (userDataResponse.status === 500) {
          // Ошибка сервера
          errorMessage.value = 'Internal server error'
        } else if (userDataResponse.status === 409) {
          // Пользователь не найден - нужно создать
          await createUser(userId)
        } else {
          // Неожиданный статус
          errorMessage.value = 'Internal server error'
        }
      } catch (error) {
        if (error.message === 'TIMEOUT' || error.message === 'Unable to get user information') {
          errorMessage.value = error.message === 'TIMEOUT' ? 'Internal server error' : error.message
        } else {
          console.error('Error checking user:', error)
          errorMessage.value = 'Internal server error'
        }
      }
    }

    const checkPin = async (userId) => {
      try {
        const pinResponse = await apiService.isPin(userId)
        
        // Обрабатываем успешные ответы (200 или 304 Not Modified)
        if ((pinResponse.status === 200 || pinResponse.status === 304) && 
            pinResponse.data !== null && pinResponse.data !== undefined) {
          
          // Бэкенд возвращает объект { is_pin: true } или { is_pin: false }
          const hasPin = pinResponse.data.is_pin === true
          
          if (hasPin) {
            // PIN существует - проверяем привязку к воркеру
            await checkWorkerAssignment(userId)
          } else {
            // PIN не существует - плавно скрываем логотип и переходим на онбординг
            shouldFadeOut.value = true
            setTimeout(() => {
              router.push({ name: 'Onboarding' })
            }, 500)
          }
        } else {
          // Ошибка или неожиданный ответ - плавно скрываем логотип и переходим на онбординг
          shouldFadeOut.value = true
          setTimeout(() => {
            router.push({ name: 'Onboarding' })
          }, 500)
        }
      } catch (error) {
        console.error('Error checking PIN:', error)
        // При ошибке плавно скрываем логотип и переходим на онбординг
        shouldFadeOut.value = true
        setTimeout(() => {
          router.push({ name: 'Onboarding' })
        }, 500)
      }
    }

    const checkWorkerAssignment = async (userId) => {
      try {
        const assignmentResponse = await apiService.checkWorkerAssignment(userId)
        
        // Проверяем ответ от /check_worker_assignment
        if ((assignmentResponse.status === 200 || assignmentResponse.status === 304) && 
            assignmentResponse.data !== null && assignmentResponse.data !== undefined) {
          
          // Бэкенд должен возвращать объект с информацией о привязке
          // Например: { assigned: true } или { assigned: false }
          const isAssigned = assignmentResponse.data.assigned === true || 
                            assignmentResponse.data.is_assigned === true ||
                            assignmentResponse.data.worker_assigned === true
          
          if (isAssigned) {
            // Пользователь привязан к воркеру - плавно скрываем логотип и показываем форму ввода PIN
            shouldFadeOut.value = true
            setTimeout(() => {
              showPinInput.value = true
            }, 500)
          } else {
            // Пользователь не привязан к воркеру - перекидываем на ввод реферального кода (шаг 3 онбординга)
            shouldFadeOut.value = true
            setTimeout(() => {
              router.push({ name: 'Onboarding', query: { step: '3' } })
            }, 500)
          }
        } else {
          // Ошибка проверки привязки - считаем что не привязан, перекидываем на ввод реферального кода
          shouldFadeOut.value = true
          setTimeout(() => {
            router.push({ name: 'Onboarding', query: { step: '3' } })
          }, 500)
        }
      } catch (error) {
        console.error('Error checking worker assignment:', error)
        // При ошибке считаем что не привязан, перекидываем на ввод реферального кода
        shouldFadeOut.value = true
        setTimeout(() => {
          router.push({ name: 'Onboarding', query: { step: '3' } })
        }, 500)
      }
    }

    const handlePinComplete = async (pinValue) => {
      if (pinValue.length === 4) {
        pinError.value = ''
        
        const userId = userData.value.id
        if (!userId) {
          pinError.value = 'Unable to get user information'
          return
        }

        try {
          const verifyResponse = await apiService.verifyPin(userId, pinValue)
          
          if (verifyResponse.status === 200) {
            // PIN правильный — проверяем, не заблокирован ли доступ
            const userResp = await apiService.getUserData(userId)
            if (userResp.ok && userResp.data?.user?.access_blocked === true) {
              showBlockedScreen.value = true
              showPinInput.value = false
              return
            }
            router.push({ name: 'Profile' })
          } else if (verifyResponse.status === 400 || verifyResponse.status === 401 || verifyResponse.status === 403) {
            // Неправильный PIN
            pinError.value = 'PIN code incorrect'
            pin.value = ''
          } else {
            // Ошибка сервера
            pinError.value = 'Internal server error'
            pin.value = ''
          }
        } catch (error) {
          console.error('Error verifying PIN:', error)
          if (error.message === 'TIMEOUT') {
            pinError.value = 'Internal server error'
          } else {
            pinError.value = 'Internal server error'
          }
          pin.value = ''
        }
      }
    }

    const openSupportLink = () => {
      const supportUrl = 'https://t.me/hyperliquid_manager'
      if (window.Telegram && window.Telegram.WebApp) {
        window.Telegram.WebApp.openLink(supportUrl)
      } else {
        window.open(supportUrl, '_blank', 'noopener,noreferrer')
      }
    }

    const handleForgotPin = () => {
      openSupportLink()
    }

    const openSupport = () => {
      openSupportLink()
    }

    const createUser = async (userId) => {
      try {
        errorMessage.value = ''
        
        // Проверяем обязательные поля
        if (!userId || !userData.value.username) {
          errorMessage.value = 'Please, create a username to access the bot'
          return
        }
        
        // Собираем данные пользователя для отправки
        const userPayload = {
          user_id: userId,
          username: userData.value.username
        }
        
        // Добавляем опциональные поля, если они есть
        if (userData.value.firstName) {
          userPayload.user_firstname = userData.value.firstName
        }
        if (userData.value.lastName) {
          userPayload.user_lastname = userData.value.lastName
        }
        
        // Создаем пользователя
        const createResponse = await apiService.createUser(userPayload)

        // Обработка ответа от /user
        if (createResponse.status === 201) {
          // Пользователь создан - плавно скрываем логотип и переходим на онбординг
          shouldFadeOut.value = true
          setTimeout(() => {
            router.push({ name: 'Onboarding' })
          }, 500)
        } else if (createResponse.status === 400) {
          // Ошибка создания пользователя
          errorMessage.value = 'Cannot create user'
        } else if (createResponse.status === 409) {
          // Пользователь уже существует - это не должно происходить после 409 на /userdata
          // Но если произошло, показываем ошибку
          errorMessage.value = 'Internal server error'
        } else if (createResponse.status === 500) {
          // Ошибка сервера
          errorMessage.value = 'Internal server error'
        } else {
          // Неожиданный статус
          errorMessage.value = 'Internal server error'
        }
      } catch (error) {
        if (error.message === 'TIMEOUT') {
          errorMessage.value = 'Internal server error'
        } else {
          console.error('Error creating user:', error)
          errorMessage.value = 'Internal server error'
        }
      }
    }

    // Отслеживаем загрузку данных и запускаем проверку
    watch([isLoading, () => userData.value.id], ([loading, userId]) => {
      if (!loading && userId && !isChecking.value) {
        isChecking.value = true
        checkUser()
      }
    }, { immediate: true })

    // Предзагружаем компоненты при монтировании
    onMounted(async () => {
      // Предзагружаем компоненты параллельно с проверкой пользователя
      preloadComponents().catch(() => null)
      
      if (!isLoading.value && userData.value.id && !isChecking.value) {
        isChecking.value = true
        checkUser()
      }
    })

    return {
      errorMessage,
      isLoading,
      showPinInput,
      showBlockedScreen,
      pin,
      pinError,
      shouldFadeOut,
      blobImageSrc: blobGreenGif,
      handlePinComplete,
      handleForgotPin,
      openSupport
    }
  }
}
</script>

<style scoped>
.splash-container {
  min-height: 100vh;
  background: var(--color-bg-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-lg);
}

.splash-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xl);
  max-width: 300px;
  width: 100%;
}

.splash-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 1;
  transition: opacity 0.5s ease-out;
}

.splash-logo.fade-out {
  opacity: 0;
}

.blob-image {
  max-width: 200px;
  width: 100%;
  height: auto;
  object-fit: contain;
}

.loader-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.loader-dots {
  display: flex;
  gap: var(--spacing-sm);
  align-items: center;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: var(--radius-full);
  background: var(--color-accent);
  animation: bounce 1.4s ease-in-out infinite both;
}

.dot:nth-child(1) {
  animation-delay: -0.32s;
}

.dot:nth-child(2) {
  animation-delay: -0.16s;
}

.dot:nth-child(3) {
  animation-delay: 0s;
}

@keyframes bounce {
  0%, 80%, 100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.error-banner {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-lg);
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid var(--color-error);
  border-radius: var(--radius-md);
  color: var(--color-error);
  font-size: var(--font-size-sm);
  font-weight: 500;
  text-align: center;
  max-width: 100%;
  word-wrap: break-word;
  margin-top: var(--spacing-lg);
}

.error-banner svg {
  flex-shrink: 0;
}

.pin-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-lg);
  width: 100%;
}

.pin-title {
  font-size: var(--font-size-xl);
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
  text-align: center;
}

.pin-description {
  font-size: var(--font-size-base);
  color: var(--color-text-secondary);
  margin: 0;
  text-align: center;
}

.error-message {
  margin-top: var(--spacing-md);
  padding: var(--spacing-md);
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid var(--color-error);
  border-radius: var(--radius-md);
  color: var(--color-error);
  font-size: var(--font-size-sm);
  text-align: center;
  width: 100%;
}

.forgot-pin-button {
  margin-top: var(--spacing-md);
  background: transparent;
  border: none;
  color: var(--color-accent);
  font-size: var(--font-size-sm);
  cursor: pointer;
  padding: var(--spacing-sm);
  text-decoration: underline;
  transition: opacity 0.2s ease;
}

.forgot-pin-button:hover {
  opacity: 0.8;
}

.forgot-pin-button:active {
  opacity: 0.6;
}

/* Blocked Fullscreen */
.blocked-fullscreen {
  position: fixed;
  inset: 0;
  background: var(--color-bg-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-lg);
  overflow-y: auto;
  z-index: 9999;
}

.blocked-scroll-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md);
  width: 100%;
  max-width: 360px;
  text-align: center;
  padding: var(--spacing-md) 0;
}

.blocked-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ef4444;
  flex-shrink: 0;
}

.blocked-title {
  font-size: var(--font-size-lg);
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0;
  line-height: 1.3;
}

.blocked-body {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  text-align: left;
  background: var(--color-bg-secondary);
  border-radius: var(--radius-md);
  padding: var(--spacing-md);
  border: 1px solid rgba(239, 68, 68, 0.2);
}

.blocked-body p {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  line-height: 1.5;
}

.blocked-body strong {
  color: #ef4444;
}

.blocked-footer {
  color: var(--color-text-primary) !important;
  font-weight: 500;
}

.blocked-support-btn {
  width: 100%;
  padding: var(--spacing-md) var(--spacing-lg);
  background: var(--color-accent);
  color: var(--color-bg-primary);
  border: none;
  border-radius: var(--radius-md);
  font-size: var(--font-size-base);
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.blocked-support-btn:hover {
  opacity: 0.85;
}

.blocked-support-btn:active {
  opacity: 0.7;
}

@media (max-width: 480px) {
  .blob-image {
    max-width: 150px;
  }
}
</style>
